import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { getWorkspace } from "../lib/workspaceStore";
import { api } from "../lib/api";
import { useMarketplacePacks } from "../hooks/useMarketplacePacks";
import { PageSkeleton } from "../components/PageSkeleton";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Link } from "react-router-dom";

export default function MarketplacePage() {
  const ws = getWorkspace();
  const qc = useQueryClient();
  const { data, isLoading, error, refetch: refetchPacks } = useMarketplacePacks();
  const { data: appDirectory, isLoading: appsLoading, error: appsError, refetch: refetchApps } = useQuery({
    queryKey: ["omnicode_marketplace_apps"],
    queryFn: async () => (await api.get("/api/marketplace/apps")).data,
  });
  const { data: libraryData, error: libraryError, refetch: refetchLibrary } = useQuery({
    queryKey: ["marketplace_app_library"],
    queryFn: async () => (await api.get("/api/marketplace/library")).data,
  });
  const [installErr, setInstallErr] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [savingAppId, setSavingAppId] = useState(null);
  const [sharedAppId, setSharedAppId] = useState(null);
  const [query, setQuery] = useState("");

  async function install(id) {
    setInstallErr(null);
    if (!ws?.id) {
      setInstallErr("Select a workspace before installing a pack.");
      return;
    }
    setBusyId(id);
    try {
      await api.post(`/api/workspaces/${ws.id}/marketplace/install`, { marketplace_pack_id: id });
      
      // Invalidate packs and marketplace
      qc.invalidateQueries({ queryKey: ["packs", ws.id] });
      qc.invalidateQueries({ queryKey: ["marketplace_packs"] });
      
      window.location.href = "/app/studio/packs";
    } catch (e) {
      setInstallErr(e?.response?.data?.detail || "Failed to install pack.");
    } finally {
      setBusyId(null);
    }
  }

  async function saveApp(app) {
    setInstallErr(null); setSavingAppId(app.id);
    try {
      await api.post("/api/marketplace/library", { app_id: app.id, app_name: app.name, app_url: app.app_url, category: app.category });
      refetchLibrary();
    } catch (e) {
      setInstallErr(e?.response?.data?.detail || "Could not save this app to your Library.");
    } finally { setSavingAppId(null); }
  }

  async function shareApp(app) {
    setInstallErr(null); setSharedAppId(app.id);
    try {
      if (navigator.share) {
        await navigator.share({ title: app.name, text: app.tagline, url: app.app_url });
      } else if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(app.app_url);
      } else {
        throw new Error("Sharing is not available in this browser.");
      }
    } catch (e) {
      if (e?.name !== "AbortError") setInstallErr(e?.message || "Could not share this app.");
    } finally {
      setSharedAppId(null);
    }
  }

  async function removeApp(appId) {
    setInstallErr(null);
    try { await api.delete(`/api/marketplace/library/${encodeURIComponent(appId)}`); await refetchLibrary(); }
    catch (e) { setInstallErr(e?.response?.data?.detail || "Could not remove this app from your Library."); }
  }

  if (isLoading || appsLoading) return <PageSkeleton />;

  const items = data?.items || [];
  const apps = appDirectory?.items || [];
  const library = libraryData?.items || [];
  const filteredApps = apps.filter((app) => `${app.name} ${app.tagline} ${app.category}`.toLowerCase().includes(query.trim().toLowerCase()));
  const err = error?.response?.data?.detail || (error ? "Failed to load marketplace packs." : null) || installErr;

  return (
    <div className="space-y-6" data-testid="marketplace-page">
      <div className="text-2xl font-semibold">App Directory</div>
      <div className="text-sm text-muted-foreground">
        A device-friendly home for web apps, saved shortcuts, and AI department packs.
      </div>

      {err && <div role="alert" className="text-sm text-red-600">{err}{error && <Button variant="outline" onClick={() => refetchPacks()}>Retry packs</Button>}</div>}
      {appsError && <div role="alert" className="text-sm text-red-600">The app directory could not be loaded. <Button variant="outline" onClick={() => refetchApps()}>Retry directory</Button></div>}
      {libraryError && <div role="alert" className="text-sm text-red-600">Your saved app library could not be loaded. <Button variant="outline" onClick={() => refetchLibrary()}>Retry saved apps</Button></div>}
      {!ws?.id && <p>Select a workspace to install department packs. <Link to="/app/workspaces" className="underline">Choose workspace</Link></p>}

      {!libraryError && <section className="rounded-2xl border bg-gradient-to-r from-violet-50 to-cyan-50 p-5 dark:from-violet-950/25 dark:to-cyan-950/20">
        <div className="flex flex-wrap items-center justify-between gap-3"><div><div className="font-semibold">My App Library</div><p className="mt-1 text-sm text-muted-foreground">Save web apps here and reopen them from any signed-in iPhone, tablet, or computer.</p></div><div className="rounded-full bg-background px-3 py-1 text-xs font-medium">{library.length} saved</div></div>
        {library.length ? <div className="mt-4 flex flex-wrap gap-2">{library.map((app) => <div key={app.id} className="flex items-center rounded-xl border bg-background text-sm"><a href={app.app_url} target="_blank" rel="noreferrer" className="px-3 py-2 transition hover:text-violet-700"><span className="font-medium">{app.app_name}</span><span className="ml-2 text-xs text-muted-foreground">Open ↗</span></a><button onClick={() => removeApp(app.app_id)} className="border-l px-2 py-2 text-xs text-muted-foreground hover:text-red-600">Remove</button></div>)}</div> : <div className="mt-4 text-sm text-muted-foreground">Save any directory app below. This is a secure shortcut library, not a native app download folder.</div>}
      </section>}

      <section className="grid gap-3 sm:grid-cols-3">
        {[['📱', 'Phone ready', 'Open any directory app in Safari, then save it to your OmniMark Library.'], ['💻', 'Laptop ready', 'Use the same signed-in Library to pick up where you left off.'], ['↗', 'Share anywhere', 'Send an approved app link to another device without moving private workspace data.']].map(([icon, title, text]) => <div key={title} className="rounded-xl border bg-muted/20 p-4"><div className="text-xl">{icon}</div><div className="mt-2 text-sm font-semibold">{title}</div><p className="mt-1 text-xs text-muted-foreground">{text}</p></div>)}
      </section>

      {!appsError && <section className="space-y-3">
        <div className="flex flex-wrap items-end justify-between gap-3"><div><div className="text-lg font-semibold">App Directory</div><p className="text-sm text-muted-foreground">Listings are published automatically only after OmniMark’s AI policy scan passes. A directory listing links to the developer’s own public app.</p></div><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search apps" className="h-9 rounded-md border bg-background px-3 text-sm" /></div>
        {filteredApps.length ? <div className="grid gap-4 md:grid-cols-2">
          {filteredApps.map((app) => <Card key={app.id}>
            <CardHeader><CardTitle className="text-base">{app.name}</CardTitle><div className="text-sm font-medium text-violet-700">{app.tagline}</div><div className="text-sm text-muted-foreground">{app.description}</div></CardHeader>
            <CardContent className="flex flex-wrap items-center justify-between gap-3"><div className="text-xs text-muted-foreground">{app.category}</div><div className="flex gap-2"><Button variant="ghost" onClick={() => shareApp(app)} disabled={sharedAppId === app.id}>{sharedAppId === app.id ? "Sharing…" : "Share"}</Button><Button variant="outline" onClick={() => saveApp(app)} disabled={savingAppId === app.id}>{savingAppId === app.id ? "Saving…" : "Save"}</Button><a href={app.app_url} target="_blank" rel="noreferrer"><Button>Open app</Button></a></div></CardContent>
          </Card>)}
        </div> : <Card><CardContent className="py-5 text-sm text-muted-foreground">No AI-cleared apps are listed yet. A customer can submit a working HTTPS app from OmniCode Builder; only policy flags stop it for review.</CardContent></Card>}
      </section>}

      {!error && <section className="space-y-3">
        <div><div className="text-lg font-semibold">AI Department Packs</div><p className="text-sm text-muted-foreground">Reusable reviewed templates for campaigns and marketing operations.</p></div>
      <div className="grid gap-4 md:grid-cols-2">
        {items.map((p) => (
          <Card key={p.id}>
            <CardHeader>
              <CardTitle className="text-base">{p.name}</CardTitle>
              <div className="text-sm text-muted-foreground">{p.description}</div>
            </CardHeader>
            <CardContent className="flex items-center justify-between">
              <div className="text-xs text-muted-foreground">
                Platform: <span className="font-medium">{p.platform}</span>
              </div>
              <Button onClick={() => install(p.id)} disabled={!ws?.id || busyId === p.id}>
                {busyId === p.id ? "Installing…" : "Install"}
              </Button>
            </CardContent>
          </Card>
        ))}
      </div>
      </section>}
    </div>
  );
}
